import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IHoveredEvent } from "./gl/CrewScheduleGL";
import {
  isIDisplayPairing,
  ICrewMember,
  ICrewEvent,
  defaultTimezone,
  isISearchPairing,
} from "src/redux/reducers/CrewScheduleReducer";
import {
  IPairingPosition,
  IFullPairingPosition,
  IPairingLogicModifier,
  IUser,
  isIPairingPosition,
  IPairing,
  IDisplayPairing,
  INonFlyingAssignment,
  IDisplayBidPairing,
  IDisplayBidNFA,
  sortCrewByDisplayOrder,
} from "@vmosolutions/oc-interfaces";
import CrewScheduleEventSummaryList, {
  ISummaryItem,
  ISummarySubHeader,
} from "src/components/CrewSchedule/CrewScheduleEventSummaryList";
import moment from "moment-timezone";
import { convertMilli2DaysHrsMin } from "src/utils/index";
import { debounce } from "lodash";
import { ClickAwayListener, Popover, Theme, useTheme, alpha, Box } from "@mui/material";
import chroma from "chroma-js";

interface IHoveredCrewSchedEventsProps {
  anchorEl: HTMLDivElement | null;
  selectedCrewEvents: ICrewEvent[];
}

interface IHoveredEventSummmary {
  hoveredEvent: IHoveredEvent;
  summaryItems: Array<ISummaryItem | ISummarySubHeader>;
}

const pairingPopoverStyles = (theme: Theme): any => ({
  zIndex: 2000,
  "& .menu": {
    backgroundColor: alpha(theme.custom.contextPopover.backgroundColor, 0.9),
    border: `1px solid ${chroma(theme.custom.contextPopover.backgroundColor).brighten(0.6).css()}`,
    color: theme.custom.contextPopover.foreGroundColor,
    boxShadow: theme.shadows[5],
    borderRadius: "5px",
    width: "min-content",
    padding: "6px 0",
    "& .pairing_id": { margin: "0px 15px", fontSize: 12 },
  },
});

export const getBasicPairingInfo = (
  pairing: IPairing | IDisplayPairing | IDisplayBidPairing,
  tz: string
): (ISummaryItem | ISummarySubHeader)[] => {
  return pairing
    ? [
        {
          key: `${pairing._id}-shortDescription`,
          value: pairing.shortDescription ? `${pairing.shortDescription.substring(0, 25)}...` : "Not Available",
          label: "Description",
        },
        {
          key: `${pairing.shortId}-shortCode`,
          value: pairing.shortId ? `${pairing.shortId}` : "Not Available",
          label: "Short Code",
        },
        {
          key: `${pairing._id}-icao`,
          value: pairing.baseICAO ?? "Unspecified",
          label: "Base",
        },
        {
          key: "pairingStart",
          value: moment.utc(pairing.start).tz(tz).format("D MMM, YYYY HH:mm z"),
          label: "Start",
        },
        {
          key: "pairingEnd",
          value: moment.utc(pairing.end).tz(tz).format("D MMM, YYYY HH:mm z"),
          label: "End",
        },
        {
          key: "pairingDuration",
          value: convertMilli2DaysHrsMin(pairing.end?.valueOf() - pairing.start?.valueOf()),
          label: "Duration",
        },
      ]
    : [];
};

/**
 * Helper function used for the ShortCode search to display paiuring summary (uses IFullPairingPosition);
 * @param positions -
 */
export const getCrewPositionInfo = (positions: IFullPairingPosition[]): (ISummaryItem | ISummarySubHeader)[] => {
  const crewPositions = positions.map((pos: IFullPairingPosition) => {
    const { crew, position } = pos;
    const crewId = crew?._id ?? "Unassigned";
    return {
      key: `${position?.title}-${crewId}`,
      value: crew?._id && crew?.lastName && crew?.firstName ? `${crew.lastName}, ${crew?.firstName}` : "Unassigned",
      label: `${position?.title}`,
    };
  });
  return crewPositions;
};

/**
 * Render basic information for attached logic modifiers on the pairing.
 * @param logicModifiers -
 */
export const getPairingLogicModifiers = (
  logicModifiers?: IPairingLogicModifier[]
): (ISummaryItem | ISummarySubHeader)[] => {
  return logicModifiers?.length > 0
    ? logicModifiers.map((lm) => ({
        value: `${lm.logicModifier.description}`,
        label: "Logic Modifier",
        key: `${lm.logicModifier._id}-logic-modifier`,
      }))
    : [];
};

const getBasicNFAInfo = (
  nfa: INonFlyingAssignment | IDisplayBidNFA,
  tz: string,
  hideNfaPriority?: boolean
): (ISummaryItem | ISummarySubHeader)[] => {
  const bidNFA = nfa as IDisplayBidNFA;
  const showTier = !hideNfaPriority && typeof bidNFA?.tier === "number";
  return nfa
    ? [
        {
          key: `${nfa._id}-type`,
          value: showTier ? `${nfa.assignmentType.title} ${bidNFA.tier}` : nfa.assignmentType.title,
          label: "Type",
        },
        {
          key: `${nfa._id}-base`,
          value: nfa.locationICAO ?? "Unspecified",
          label: "Base",
        },
        {
          key: `${nfa._id}-start`,
          value: moment.utc(nfa.start).tz(tz).format("D MMM, YYYY HH:mm z"),
          label: "Start",
        },
        {
          key: `${nfa._id}-end`,
          value: moment.utc(nfa.end).tz(tz).format("D MMM, YYYY HH:mm z"),
          label: "End",
        },
        {
          key: `${nfa._id}-nfaDuration`,
          value: convertMilli2DaysHrsMin(nfa.end?.valueOf() - nfa.start?.valueOf()),
          label: "Duration",
        },
      ]
    : [];
};

// Ensures that if the user is moving the mouse across several events, the popover delay will reset accordingly.
const debouncedOpen = debounce((setOpen: (state: boolean) => void, state: boolean): void => {
  setOpen(state);
}, 1000);

/**
 * This function is required as crew names don't seem to be stored using the same format.
 * @param position - Given a crew position, return name if one is found, unassigned if not.
 */
const returnCrewName = (pos: IPairingPosition | IUser): string | null => {
  const firstName = (pos as IUser)?.firstName
    ? (pos as IUser).firstName
    : (pos as IPairingPosition)?.givenName
      ? (pos as IPairingPosition).givenName
      : null;
  const lastName = (pos as IUser)?.lastName
    ? (pos as IUser).lastName
    : (pos as IPairingPosition)?.surname
      ? (pos as IPairingPosition).surname
      : null;
  return !firstName && !lastName ? "Not Found" : `${lastName ?? ""}, ${firstName ?? ""}`;
};

export const HoveredCrewSchedEvents = (props: IHoveredCrewSchedEventsProps): JSX.Element => {
  const { anchorEl } = props; //selectedCrewEvents
  const theme: Theme = useTheme();
  const [open, setOpen] = useState(false);
  const crew = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const positionsArray = useSelector((state: IRootState) => state.structures.customerStructure.positions);
  const { hoveredEvents, anchorPos, windowId } = useSelector((state: IRootState) => state.crewSchedule.hoveredEvents);

  // TimezoneId is derived from windowId passed in from hovered event (not props)
  // Passing in timezone from props causes hovered event to render once for every window instance and changes value.
  const windowProperties = useSelector((state: IRootState) => state.crewSchedule.windowProperties[windowId]);
  const { timezoneId } = windowProperties?.displayTimezone ?? defaultTimezone;
  const hideNfaPriority = useSelector(
    (state: IRootState) => state.crewSchedule.biddingSettings?.hideNfaPriority ?? false
  );

  const hoveredEventSummaryItems: IHoveredEventSummmary[] = useMemo(() => {
    if (!hoveredEvents || hoveredEvents.length === 0) {
      return [];
    }

    return hoveredEvents.map((hoveredEvent: IHoveredEvent) => {
      const activelySelectedCrew = hoveredEvent?.crewId;
      const positions: (IPairingPosition | IUser | { crew_id: string; position: string })[] =
        isIDisplayPairing(hoveredEvent?.event) || isISearchPairing(hoveredEvent?.event)
          ? hoveredEvent.event.positions
          : hoveredEvent?.event?.crew;
      const isBidPairing = !!(hoveredEvent?.event as IDisplayBidPairing)?.bidPairingId;
      const crewPositions = positions
        .map((position: IPairingPosition | IUser) => {
          const posCrewName = returnCrewName(position);

          if (isIPairingPosition(position)) {
            const crewMember = crew.find((crew: ICrewMember) => position.crew_id === crew?._id);
            // If crewName comes back as unAssigned, check crewMember info.
            const crewName =
              posCrewName === "Not Found" && crewMember?.firstName && crewMember?.lastName
                ? `${crewMember?.lastName}, ${crewMember?.firstName}`
                : posCrewName?.length > 0 && posCrewName !== "Not Found"
                  ? posCrewName
                  : "Unassigned";

            const displayOrder = positionsArray.find((pos) => pos.title === position.position).displayOrder;
            const posBidOn =
              isBidPairing &&
              (hoveredEvent?.event as IDisplayBidPairing)?.positionBidOn === position.position &&
              !position?.crew_id
                ? "(Bid On)"
                : "";
            const awardedTo =
              isBidPairing &&
              (hoveredEvent?.event as IDisplayBidPairing)?.awarded &&
              position?.crew_id === activelySelectedCrew
                ? "(Awarded)"
                : "";
            return {
              key: `${position.position}-${position.crew_id}`,
              value: crewName,
              label: `${position.position} ${posBidOn} ${awardedTo}`,
              position: { displayOrder }, // Add position displayOrder so that sort function works.
            };
          } else {
            const displayOrder = positionsArray.find(
              (pos) => pos.title === position.crewInfo?.positions?.[0].title
            ).displayOrder;
            return {
              value: posCrewName,
              label: position.crewInfo?.positions?.[0].title,
              key: `${position._id}-crewMember`,
              position: { displayOrder }, // Add position displayOrder so that sort function works.
            };
          }
        })
        //@ts-ignore
        .sort(sortCrewByDisplayOrder);

      const logicModifiers = isIDisplayPairing(hoveredEvent?.event)
        ? getPairingLogicModifiers(hoveredEvent?.event?.logicModifiers)
        : [];

      const basicInfo = isIDisplayPairing(hoveredEvent?.event)
        ? getBasicPairingInfo(hoveredEvent.event, timezoneId)
        : getBasicNFAInfo(hoveredEvent.event, timezoneId, hideNfaPriority);
      const summaryItems: (ISummaryItem | ISummarySubHeader | null)[] = [
        ...basicInfo,
        ...crewPositions,
        ...logicModifiers,
      ];
      return { hoveredEvent, summaryItems };
    });
  }, [crew, hoveredEvents, positionsArray, timezoneId, hideNfaPriority]);

  // Make certain any change in the hovered event summary items triggers a debounce call.
  // Clickawayhandler added in the rare situation where the user moves the mouse so rapidly that the change effect doesn't register the hover.
  const displaySummary: boolean = hoveredEventSummaryItems?.length > 0;

  useEffect(() => debouncedOpen(setOpen, displaySummary), [setOpen, displaySummary]);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Popover
        sx={{ ...pairingPopoverStyles(theme), position: "inherit" }}
        anchorEl={anchorEl}
        anchorReference="anchorPosition"
        anchorPosition={{ top: anchorPos?.y - 0, left: anchorPos?.x - 0 }}
        open={open && hoveredEventSummaryItems?.length > 0}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 0, horizontal: 0 }}
        transformOrigin={{ vertical: 0, horizontal: 0 }}
        disableScrollLock={true}
      >
        {hoveredEventSummaryItems.map((hoveredEventSummaryItem: IHoveredEventSummmary, index: number) => {
          const { summaryItems } = hoveredEventSummaryItem;
          return (
            <Box className="menu" key={`${index}-summaryItem`} onPointerLeave={() => setOpen(false)}>
              <CrewScheduleEventSummaryList hideTitle={true} items={summaryItems} />
            </Box>
          );
        })}
      </Popover>
    </ClickAwayListener>
  );
};
